// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Forge Casino — Paytable Calculator
// Calculates wins based on paylines and symbol combinations
// ================================================================

/**
 * Standard paylines for a 5x3 slot grid
 * Each payline is an array of row indices for each reel
 * Example: [1,1,1,1,1] = middle row across all 5 reels
 */
const PAYLINES_5x3 = [
  [1, 1, 1, 1, 1],  // Line 1: Middle
  [0, 0, 0, 0, 0],  // Line 2: Top
  [2, 2, 2, 2, 2],  // Line 3: Bottom
  [0, 1, 2, 1, 0],  // Line 4: V-shape
  [2, 1, 0, 1, 2],  // Line 5: Inverted V
  [0, 0, 1, 2, 2],  // Line 6: Ascending
  [2, 2, 1, 0, 0],  // Line 7: Descending
  [1, 0, 0, 0, 1],  // Line 8: Top dip
  [1, 2, 2, 2, 1],  // Line 9: Bottom dip
  [0, 1, 1, 1, 0],  // Line 10: Slight V
  [2, 1, 1, 1, 2],  // Line 11: Slight inverted V
  [0, 1, 0, 1, 0],  // Line 12: Zigzag top
  [2, 1, 2, 1, 2],  // Line 13: Zigzag bottom
  [1, 0, 1, 0, 1],  // Line 14: Wave top
  [1, 2, 1, 2, 1],  // Line 15: Wave bottom
  [0, 0, 1, 0, 0],  // Line 16: Top with center dip
  [2, 2, 1, 2, 2],  // Line 17: Bottom with center rise
  [1, 0, 1, 2, 1],  // Line 18: W-shape
  [1, 2, 1, 0, 1],  // Line 19: M-shape
  [0, 2, 0, 2, 0],  // Line 20: Extreme zigzag
];

/**
 * Standard paylines for a 3x3 (classic) slot
 */
const PAYLINES_3x3 = [
  [1, 1, 1],  // Middle
  [0, 0, 0],  // Top
  [2, 2, 2],  // Bottom
  [0, 1, 2],  // Diagonal down
  [2, 1, 0],  // Diagonal up
];

/**
 * Check a single payline for wins
 * @param {Array<Array<Object>>} grid - The spin result grid [reel][row]
 * @param {Array<number>} payline - Row indices for each reel
 * @returns {Object|null} Win info or null
 */
function checkPayline(grid, payline) {
  const symbols = payline.map((rowIdx, reelIdx) => grid[reelIdx][rowIdx]);

  // Find the first non-wild symbol
  let matchSymbol = null;
  for (const sym of symbols) {
    if (!sym.isWild && !sym.isScatter) {
      matchSymbol = sym;
      break;
    }
  }

  if (!matchSymbol) {
    // All wilds — use the highest value symbol payout
    matchSymbol = symbols[0];
  }

  // Count consecutive matches from left to right (wilds count)
  let matchCount = 0;
  for (const sym of symbols) {
    if (sym.id === matchSymbol.id || sym.isWild) {
      matchCount++;
    } else {
      break;
    }
  }

  // Need at least 3 matching symbols for a win (or 2 for some high-value symbols)
  if (matchCount < 3) return null;

  const payout = matchSymbol.payout?.[matchCount] || 0;
  if (payout === 0) return null;

  return {
    symbol: matchSymbol,
    count: matchCount,
    payout,
    positions: payline.slice(0, matchCount).map((rowIdx, reelIdx) => ({ reel: reelIdx, row: rowIdx })),
  };
}

/**
 * Check all paylines and calculate total win
 * @param {Array<Array<Object>>} grid - The spin result grid
 * @param {number} betPerLine - Bet amount per payline
 * @param {Array<Array<number>>} paylines - Paylines to check
 * @returns {Object} Total win info
 */
function calculateWins(grid, betPerLine, paylines = null) {
  const reels = grid.length;
  const activePaylines = paylines || (reels === 3 ? PAYLINES_3x3 : PAYLINES_5x3.slice(0, 20));

  const wins = [];
  let totalPayout = 0;

  for (let i = 0; i < activePaylines.length; i++) {
    const win = checkPayline(grid, activePaylines[i]);
    if (win) {
      const lineWin = win.payout * betPerLine;
      wins.push({
        ...win,
        lineIndex: i + 1,
        lineWin,
      });
      totalPayout += lineWin;
    }
  }

  // Check for scatter wins (appear anywhere on the grid)
  let scatterCount = 0;
  const scatterPositions = [];
  for (let r = 0; r < grid.length; r++) {
    for (let row = 0; row < grid[r].length; row++) {
      if (grid[r][row].isScatter) {
        scatterCount++;
        scatterPositions.push({ reel: r, row });
      }
    }
  }

  let scatterWin = null;
  if (scatterCount >= 3) {
    const scatterPayout = scatterCount === 3 ? 5 : scatterCount === 4 ? 20 : 100;
    const totalBet = betPerLine * activePaylines.length;
    scatterWin = {
      count: scatterCount,
      payout: scatterPayout * totalBet,
      positions: scatterPositions,
      triggersBonus: scatterCount >= 3,
    };
    totalPayout += scatterWin.payout;
  }

  return {
    wins,
    scatterWin,
    totalPayout,
    isWin: totalPayout > 0,
    totalBet: betPerLine * activePaylines.length,
    netResult: totalPayout - (betPerLine * activePaylines.length),
  };
}

/**
 * Calculate theoretical RTP for a given symbol set and payline configuration
 * Used for regulatory compliance (GLI-11 verification)
 * @param {Array} symbols - Symbol definitions
 * @param {Array<Array<number>>} paylines - Paylines
 * @param {number} simulations - Number of simulations to run
 * @returns {Object} RTP statistics
 */
function calculateTheoreticalRTP(symbols, paylines, simulations = 100000) {
  let totalBet = 0;
  let totalReturn = 0;

  for (let i = 0; i < simulations; i++) {
    const grid = [];
    const totalWeight = symbols.reduce((sum, s) => sum + s.weight, 0);

    for (let r = 0; r < (paylines[0]?.length || 3); r++) {
      const reel = [];
      for (let row = 0; row < 3; row++) {
        let rand = Math.random() * totalWeight;
        for (const sym of symbols) {
          rand -= sym.weight;
          if (rand <= 0) {
            reel.push(sym);
            break;
          }
        }
      }
      grid.push(reel);
    }

    const betPerLine = 1;
    const result = calculateWins(grid, betPerLine, paylines);
    totalBet += result.totalBet;
    totalReturn += result.totalPayout;
  }

  const rtp = (totalReturn / totalBet) * 100;
  return {
    rtp: rtp.toFixed(2) + '%',
    rtpValue: rtp,
    simulations,
    totalBet,
    totalReturn,
    houseEdge: (100 - rtp).toFixed(2) + '%',
    meetsAGLC: rtp >= 85, // Alberta minimum
    meetsGLI11: rtp >= 85 && rtp <= 100,
  };
}

export {
  PAYLINES_5x3,
  PAYLINES_3x3,
  checkPayline,
  calculateWins,
  calculateTheoreticalRTP,
};
